import { FC, memo, useRef } from 'react'

import InfiniteScroll from 'react-infinite-scroller'

import { useI18n } from '@tribeplatform/react-components/i18n'
import { CHATS_QUERY, useChats } from '@tribeplatform/react-sdk/hooks'
import { EmptyState } from '@tribeplatform/react-ui-kit/EmptyState'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { ChatListItem } from './Chat/ChatListItem/ChatListItem.js'
import { LoadingNewChatsIndicator } from './Sidebar/LoadingNewChatsIndicator.js'

export const MessagingDropdownList: FC<{ closeDropdown: () => void }> = memo(
  ({ closeDropdown }) => {
    const { $t } = useI18n()
    const scrollParentRef = useRef<HTMLDivElement>()

    const {
      data,
      fetchNextPage,
      hasNextPage = false,
      isInitialLoading,
    } = useChats({
      variables: CHATS_QUERY,
      useInfiniteQueryOptions: {
        /**
         * Pages may produce different result after a refetch.
         * The results must be consolidated and deduplicated manually.
         */
        refetchInterval: false,
      },
    })

    const items = data?.pages.flatMap(({ data }) =>
      data.map(chat => (
        <li key={chat._id} className="p-3">
          <ChatListItem
            chat={chat}
            className="p-3 rounded-base hover:bg-surface-hovered"
            onClick={closeDropdown}
          />
        </li>
      )),
    )

    return (
      <div className="overflow-y-auto max-h-96" ref={scrollParentRef}>
        <InfiniteScroll
          pageStart={0}
          loadMore={fetchNextPage}
          hasMore={hasNextPage || false}
          useWindow={false}
          getScrollParent={() => scrollParentRef.current}
        >
          <ul className="bg-surface overflow-hidden divide-y divide-line-subdued">
            {items}
            {isInitialLoading ? <LoadingNewChatsIndicator /> : null}
            {!items?.length && !isInitialLoading ? (
              <li className="py-10">
                <EmptyState
                  title={$t({
                    defaultMessage: 'Nothing here!',
                    id: 'Generics.NothingHere',
                  })}
                  description={$t({
                    defaultMessage: 'You don’t have any messages.',
                    id: 'Messaging.ChatList.EmptyState.Description',
                  })}
                  icon={<SvgIcon name="inbox" />}
                />
              </li>
            ) : null}
          </ul>
        </InfiniteScroll>
      </div>
    )
  },
)
